import { ICloudbase } from '@cloudbase/types'
import { ICloudbaseComponent } from '@cloudbase/types/component'
import { StorageFileApi } from './storage-file-api-neo'
import { StorageBucketApi } from './storage-bucket-api-neo'
import { assertValidBucketId } from './validation'
import { CloudbaseStorage, COMPONENT_NAME, ICloudbaseContext } from './cloudbase-storage'
import { ClassicStorageFileApi } from './storage-file-api-classic'

// 重新导出，保持对外 API 不变（这些符号原先从本入口文件导出）
export { COMPONENT_NAME, CloudbaseStorage } from './cloudbase-storage'
export type { ICloudbaseContext } from './cloudbase-storage'
export { ClassicStorageFileApi } from './storage-file-api-classic'
export { StorageFileApi } from './storage-file-api-neo'

declare const cloudbase: ICloudbase

const storage = new CloudbaseStorage()
const component: ICloudbaseComponent = {
  name: COMPONENT_NAME,
  entity: {
    uploadFile: storage.uploadFile,
    deleteFile: storage.deleteFile,
    getTempFileURL: storage.getTempFileURL,
    downloadFile: storage.downloadFile,
    getUploadMetadata: storage.getUploadMetadata,
    copyFile: storage.copyFile,
    getFileInfo: storage.getFileInfo,
    isGateWay: storage.isGateWay,
  },
}

/**
 * `app.storage` 命名空间（分流器）。继承 Bucket 管理能力，并提供 `from()` 分流。
 *
 * `from()` 分流到两个**对等且独立**的客户端——`ClassicStorageFileApi`（传统环境） 与 `StorageFileApi`（Neo 环境）。
 *
 * - `from()`（不传 bucketId）：返回 `ClassicStorageFileApi`；
 * - `from(bucketId)`：返回 `StorageFileApi`（`bucketId + objectName` 语义）；
 * - `listBuckets()` 等 Bucket 管理（继承自 `StorageBucketApi`）为 Neo 环境专属能力。
 */
export class StorageClient extends StorageBucketApi {
  /**
   * 进入对象操作客户端。
   *
   * - `from()`（不传 bucketId）：返回传统环境对象操作客户端；
   * - `from(bucketId)`：返回 Neo 环境对象操作（`bucketId + objectName` 语义）客户端。
   *
   * @remarks 传入 `bucketId` 时返回的客户端仅 Neo 环境可用。`bucketId` 为空或格式非法将抛错（格式见 `assertValidBucketId`）。
   */
  from(): ClassicStorageFileApi
  from<const B extends string>(bucketId: B): StorageFileApi
  from(bucketId?: string): ClassicStorageFileApi | StorageFileApi {
    if (bucketId) {
      assertValidBucketId(bucketId, 'from')
      return new StorageFileApi(this.http, bucketId, this.shouldThrowOnError)
    }
    return new ClassicStorageFileApi(this.http.ctx, this.shouldThrowOnError)
  }
}

const supabaseComponent: ICloudbaseComponent = {
  name: `${COMPONENT_NAME}/supabase`,
  IIFE: true,
  entity(this: { prototype: object }) {
    // 在 IIFE 中，this 是 Cloudbase 类（构造函数），不是实例
    // 所以我们需要使用 getter 来延迟获取实例的 config 和 request

    // 使用 WeakMap 缓存每个实例的 storage 对象
    const storageCache = new WeakMap<any, StorageClient>()

    // 定义 storage 属性的 getter，每个实例只创建一次
    Object.defineProperty(this.prototype, 'storage', {
      get() {
        // 这里的 this 是 cloudbase 实例

        // 如果已经创建过，直接返回缓存的实例
        if (storageCache.has(this)) {
          return storageCache.get(this)
        }

        // 创建动态 context，从当前实例获取 config 和 request
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const instance = this
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const context = new Proxy<ICloudbaseContext>({} as ICloudbaseContext, {
          get: (_target, prop) => {
            if (prop === 'config') {
              return instance.config
            }
            if (prop === 'request') {
              return instance.request
            }
            return undefined
          },
        })

        // 创建新的 storage 实例并缓存
        const storage = new StorageClient(context)
        storageCache.set(this, storage)
        return storage
      },
      configurable: true,
      enumerable: true,
    })
  },
}

try {
  cloudbase.registerComponent(component)
  cloudbase.registerComponent(supabaseComponent)
} catch (e) {}

export function registerStorage(app: Pick<ICloudbase, 'registerComponent'>) {
  try {
    app.registerComponent(component)
    app.registerComponent(supabaseComponent)
  } catch (e) {
    console.warn(e)
  }
}

try {
  (window as any).registerStorage = registerStorage
} catch (e) {}
